import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiPost } from '../api/client';
import type { ChangePasswordRequest, MessageResponse } from '../types/api';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import './ChangePasswordPage.css';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{10,}$/;

export default function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  function validate(): string | null {
    if (!PASSWORD_REGEX.test(newPassword)) {
      return 'Password must be at least 10 characters with uppercase, lowercase, digit, and special character.';
    }
    if (newPassword !== confirmPassword) {
      return 'Passwords do not match.';
    }
    if (currentPassword === newPassword) {
      return 'New password must be different from current password.';
    }
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const body: ChangePasswordRequest = {
        currentPassword,
        newPassword,
        confirmPassword,
      };
      const resp = await apiPost<MessageResponse>('/api/auth/change-password', body);
      setSuccess(resp.message || 'Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="change-password-page">
      <div className="change-password-container">
        <h2 className="change-password-title">Change Password</h2>

        <AlertMessage type="error" message={error} />
        <AlertMessage type="success" message={success} />

        {!success && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="currentPassword">Current password</label>
              <input
                id="currentPassword"
                className="form-input"
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                className="form-input"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
              <p className="password-hint">
                At least 10 characters with uppercase, lowercase, digit, and special character.
              </p>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="confirmNewPassword">Confirm new password</label>
              <input
                id="confirmNewPassword"
                className="form-input"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? <LoadingSpinner small /> : 'Change Password'}
            </button>
          </form>
        )}

        <Link to="/" className="change-password-back">&larr; Back to Dashboard</Link>
      </div>
    </div>
  );
}
